import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

const EMPTY = {
  title: "", description: "", date: "", time: "", order: 0,
  location: "", category: "", status: "confirmed", source: "", source_url: "",
};

export default function AdminPrograma() {
  const [events, setEvents] = useState([]);
  const [newEv, setNewEv] = useState(EMPTY);

  const load = () => api.get("/admin/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newEv.title) return toast.error("Añade un título");
    try {
      await api.post("/events", newEv);
      setNewEv(EMPTY);
      load();
      toast.success("Evento añadido");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const update = async (ev) => {
    try {
      await api.put(`/events/${ev.id}`, {
        title: ev.title, description: ev.description, date: ev.date, time: ev.time, order: ev.order,
        location: ev.location || "", category: ev.category || "", status: ev.status || "confirmed",
        source: ev.source || "", source_url: ev.source_url || "",
      });
      toast.success("Actualizado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar evento?")) return;
    await api.delete(`/events/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-programa">
      <header>
        <h1 className="font-display font-black text-3xl">Programa</h1>
        <p className="text-muted-foreground">Gestiona la agenda de la Feria.</p>
      </header>

      <div className="bg-white rounded-2xl border border-border p-5">
        <h2 className="font-display font-bold mb-3">Nuevo evento</h2>
        <div className="grid sm:grid-cols-5 gap-2">
          <input placeholder="Título" value={newEv.title} onChange={(e) => setNewEv({ ...newEv, title: e.target.value })} className="sm:col-span-2 rounded-lg border border-border px-3 py-2" data-testid="ev-new-title" />
          <input type="date" value={newEv.date} onChange={(e) => setNewEv({ ...newEv, date: e.target.value })} className="rounded-lg border border-border px-3 py-2" data-testid="ev-new-date" />
          <input placeholder="Hora" value={newEv.time} onChange={(e) => setNewEv({ ...newEv, time: e.target.value })} className="rounded-lg border border-border px-3 py-2" data-testid="ev-new-time" />
          <button onClick={create} className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-full font-bold" data-testid="ev-add-btn">
            <Plus size={16} /> Añadir
          </button>
        </div>
        <input placeholder="Descripción" value={newEv.description} onChange={(e) => setNewEv({ ...newEv, description: e.target.value })} className="w-full mt-2 rounded-lg border border-border px-3 py-2" data-testid="ev-new-desc" />
        <div className="grid sm:grid-cols-3 gap-2 mt-2">
          <input placeholder="Lugar" value={newEv.location} onChange={(e) => setNewEv({ ...newEv, location: e.target.value })} className="rounded-lg border border-border px-3 py-2" />
          <input placeholder="Categoría" value={newEv.category} onChange={(e) => setNewEv({ ...newEv, category: e.target.value })} className="rounded-lg border border-border px-3 py-2" />
          <select value={newEv.status} onChange={(e) => setNewEv({ ...newEv, status: e.target.value })} className="rounded-lg border border-border px-3 py-2">
            <option value="confirmed">Confirmado</option>
            <option value="pending_confirmation">Pendiente de confirmar</option>
          </select>
          <input placeholder="Fuente interna" value={newEv.source} onChange={(e) => setNewEv({ ...newEv, source: e.target.value })} className="rounded-lg border border-border px-3 py-2" />
          <input placeholder="URL de fuente interna" value={newEv.source_url} onChange={(e) => setNewEv({ ...newEv, source_url: e.target.value })} className="sm:col-span-2 rounded-lg border border-border px-3 py-2" />
        </div>
      </div>

      <div className="space-y-2">
        {events.map((ev) => (
          <div key={ev.id} className="bg-white rounded-2xl border border-border p-4 grid sm:grid-cols-[2fr_1fr_1fr_auto] gap-2 items-center" data-testid={`ev-row-${ev.id}`}>
            <input value={ev.title} onChange={(e) => setEvents((arr) => arr.map((x) => x.id === ev.id ? { ...x, title: e.target.value } : x))} className="rounded-lg border border-border px-2 py-2 text-sm font-bold" />
            <input type="date" value={ev.date || ""} onChange={(e) => setEvents((arr) => arr.map((x) => x.id === ev.id ? { ...x, date: e.target.value } : x))} className="rounded-lg border border-border px-2 py-2 text-sm" />
            <input value={ev.time || ""} onChange={(e) => setEvents((arr) => arr.map((x) => x.id === ev.id ? { ...x, time: e.target.value } : x))} placeholder="Hora" className="rounded-lg border border-border px-2 py-2 text-sm" />
            <div className="flex gap-1">
              <button onClick={() => update(ev)} className="h-9 w-9 grid place-items-center rounded-lg bg-primary text-primary-foreground" data-testid={`ev-save-${ev.id}`}><Save size={14} /></button>
              <button onClick={() => remove(ev.id)} className="h-9 w-9 grid place-items-center rounded-lg bg-destructive text-destructive-foreground" data-testid={`ev-delete-${ev.id}`}><Trash2 size={14} /></button>
            </div>
            <textarea value={ev.description || ""} onChange={(e) => setEvents((arr) => arr.map((x) => x.id === ev.id ? { ...x, description: e.target.value } : x))} rows={2} className="sm:col-span-4 rounded-lg border border-border px-2 py-2 text-sm" />
            <input value={ev.location || ""} onChange={(e) => setEvents((arr) => arr.map((x) => x.id === ev.id ? { ...x, location: e.target.value } : x))} placeholder="Lugar" className="rounded-lg border border-border px-2 py-2 text-sm" />
            <input value={ev.category || ""} onChange={(e) => setEvents((arr) => arr.map((x) => x.id === ev.id ? { ...x, category: e.target.value } : x))} placeholder="Categoría" className="rounded-lg border border-border px-2 py-2 text-sm" />
            <select value={ev.status || "confirmed"} onChange={(e) => setEvents((arr) => arr.map((x) => x.id === ev.id ? { ...x, status: e.target.value } : x))} className="rounded-lg border border-border px-2 py-2 text-sm">
              <option value="confirmed">Confirmado</option>
              <option value="pending_confirmation">Pendiente de confirmar</option>
            </select>
            <div />
            <input value={ev.source || ""} onChange={(e) => setEvents((arr) => arr.map((x) => x.id === ev.id ? { ...x, source: e.target.value } : x))} placeholder="Fuente interna" className="sm:col-span-2 rounded-lg border border-border px-2 py-2 text-sm" />
            <input value={ev.source_url || ""} onChange={(e) => setEvents((arr) => arr.map((x) => x.id === ev.id ? { ...x, source_url: e.target.value } : x))} placeholder="URL de fuente interna" className="sm:col-span-2 rounded-lg border border-border px-2 py-2 text-sm" />
          </div>
        ))}
      </div>
    </div>
  );
}
